import { ADMINS } from '@/shared/authorization/permissionGroups';
import type { NavHandle } from '@/shared/navigation/nav-tree';

import { lazyRoute } from './lazy-route';
import { sectionChildren } from './section-index-redirect';

import type { RouteObject } from 'react-router-dom';

// /management-reports/reports/drp
const drpReport = lazyRoute(
  'drp',
  () =>
    import('@/containers/ManagementReportContainers/OperationReportContainer/OperationReportContainer'),
  { handle: { label: 'DRP' } }
);

// /management-reports/reports/lrp
const lrpReport = lazyRoute(
  'lrp',
  () => import('@/features/reports').then(m => ({ default: m.LRPReports })),
  { handle: { label: 'LRP' } }
);

// /management-reports/reports — no element of its own: the DRP/LRP switcher it
// used to render is the sidebar tree now.
const operationReport: RouteObject = {
  path: 'reports',
  handle: { label: 'Reports', icon: 'Assessment' } satisfies NavHandle,
  children: sectionChildren([drpReport, lrpReport])
};

// /management-reports/sms-usage-tracking/culmulative-report
const culmulativeReport = lazyRoute(
  'culmulative-report',
  () => import('@/containers/ManagementReportContainers/SmsUsageTracking/CulmulativeReport')
);

// /management-reports/sms-usage-tracking/detailed-report
const detailedReport = lazyRoute(
  'detailed-report',
  () => import('@/containers/ManagementReportContainers/SmsUsageTracking/DetailedReport')
);

// /management-reports/sms-usage-tracking — admin-only, mirroring the module's old tab gating
const smsUsageTracking = lazyRoute(
  'sms-usage-tracking',
  () =>
    import('@/containers/ManagementReportContainers/SmsUsageTracking/SmsUsageTrackingContainer'),
  {
    children: [culmulativeReport, detailedReport],
    handle: { label: 'SMS Usage Tracking', icon: 'Sms', gate: ADMINS }
  }
);

// Unimplemented reports — render in the tree but inert, like the old disabled tabs.
const stubReports: RouteObject[] = (
  [
    ['agent-report', 'Agent Report', 'SupportAgent'],
    ['geo-report', 'Geo Report', 'Map'],
    ['user-report', 'User Report', 'Group']
  ] as const
).map(([path, label, icon]) => ({
  path,
  element: <div>{label}</div>,
  handle: { label, icon, disabledWhen: () => true } satisfies NavHandle
}));

// /management-reports
export const managementReportsRoutes: RouteObject = lazyRoute(
  '/management-reports',
  () => import('@/modules/ManagementReportsModule').then(m => ({ default: m.ManagementReports })),
  { children: sectionChildren([operationReport, ...stubReports, smsUsageTracking]) }
);
